import { useEffect, useRef } from "react";

import { useAppSelector } from "app/hooks";
import { useDocumentVisible } from "hooks/use-document-visible.hook";
import { useGetEventsQuery, useGetEventsWindowQuery } from "services/events";
import { type EventFilters } from "services/types/events.type";

import { selectFormOpen } from "../scheduler-slice";

type Options = NonNullable<Parameters<typeof useGetEventsQuery>[1]>;

/**
 * The scheduler's events query (ENG-2225). With `refetchScoping` off this is exactly
 * `useGetEventsQuery`. With it on:
 *
 * - it reads `getEventsWindow`, whose entries outlive their subscribers for a few minutes,
 *   so going back to a recently loaded week does not refetch it;
 * - polling stops while the tab is hidden or the appointment form is open, and resuming with
 *   data older than the poll interval refetches once. The poll itself stays: moving an
 *   appointment (a new time or provider, no status change) is not broadcast over the clinic
 *   socket, so for another user's moves and edits the poll is the only way they reach this
 *   grid. Creates are broadcast: the backend sends a new appointment's first status as a
 *   status update.
 *
 * The pause switches the interval to 0 rather than using RTK's `skipPollingIfUnfocused`,
 * whose timer reads a focus flag captured when the poll was scheduled, so a refetch while
 * hidden could stall polling for two intervals. The form state is Redux's `selectFormOpen`,
 * which every form opener sets (most never touch the provider's `editingForm`).
 *
 * Both hooks are always called, the inactive one skipped, so a flag change mid-session swaps
 * cleanly instead of changing hook order.
 */
export const useSchedulerEvents = (
  filters: EventFilters,
  options: Options,
  refetchScoping: boolean
) => {
  const formOpen = useAppSelector(selectFormOpen);
  const visible = useDocumentVisible();
  const pollingInterval = options.pollingInterval ?? 0;
  const paused = !visible || formOpen;

  const legacy = useGetEventsQuery(filters, {
    ...options,
    skip: options.skip || refetchScoping
  });
  const windowed = useGetEventsWindowQuery(filters, {
    ...options,
    skip: options.skip || !refetchScoping,
    ...(refetchScoping && pollingInterval && paused
      ? { pollingInterval: 0 }
      : {})
  });

  const { refetch, fulfilledTimeStamp, isUninitialized } = windowed;
  const wasPaused = useRef(paused);
  useEffect(() => {
    const resumed = wasPaused.current && !paused;
    wasPaused.current = paused;
    if (
      resumed &&
      refetchScoping &&
      !options.skip &&
      pollingInterval > 0 &&
      !isUninitialized &&
      fulfilledTimeStamp !== undefined &&
      Date.now() - fulfilledTimeStamp >= pollingInterval
    ) {
      refetch();
    }
  }, [
    paused,
    refetchScoping,
    options.skip,
    pollingInterval,
    isUninitialized,
    fulfilledTimeStamp,
    refetch
  ]);

  return refetchScoping ? windowed : legacy;
};
